// ZAMBA Product Ordering Code Catalog
// =====================================
// Source: "Product Ordering Code.xlsx" (uploaded May 2026).
// Every model entry below was copied STRICTLY from the spreadsheet — same
// fields, same order, same numeric codes, same notes. Do NOT reinterpret.
//
// Each model entry defines:
//   • baseFields[]      → fields encoded into the "Base Unit Code" (B-{model}…)
//   • accessoryFields[] → fields encoded into the "Accessory Code"  (A-…)
//
// Field shape:
//   { key, label,
//     type: "select" | "merv" | "ral" | "nozzle_count",
//     options: [{ value, label }],   // required for "select"
//     default: <value>,
//     standard: <value>,             // greyed-out "standard" hint in UI
//     note: "Factory Installed" | "Separate in a box" | "Standard" | "",
//     visibleWhen?: (cfg) => boolean,
//     auto?: true                    // populated from selection engine; read-only
//   }
//
// Encoding rules:
//   • Most options are encoded as a single digit.
//   • RAL color: when value === 3, append "(xxxx)" with the 4-digit code chosen.
//   • MERV filter: encoded as 2-digit value wrapped in (xx).
//   • Nozzle count: numeric value wrapped in (xx) or (xxx).
//   • Length codes: literal digit (2,3,4…). For Model C/C-1: 2,4,6,8,10 (10 → "X").

// =====================================
// Common option groups (re-used)
// =====================================
const MODE = [
  { value: 0, label: "Cooling and heating" },
  { value: 1, label: "Cooling only" },
  { value: 2, label: "Heating only" },
];
const VALVE = [
  { value: 0, label: "No valve" },
  { value: 1, label: "PICV" },
  { value: 2, label: "PIBCV" },
  { value: 3, label: "On/Off" },
];
const ACTUATOR = [
  { value: 0, label: "No actuator" },
  { value: 1, label: "On/Off" },
  { value: 2, label: "0–10 V" },
];
const OPEN_CLOSE = [
  { value: 0, label: "Nothing" },
  { value: 1, label: "Normally Open" },
  { value: 2, label: "Normally Closed" },
];
const COMMUNICATION = [
  { value: 0, label: "No comm" },
  { value: 1, label: "BACnet MSTP" },
  { value: 2, label: "ModBUS Slave RTU" },
];
const FINISH = [
  { value: 1, label: "Gloss" },
  { value: 2, label: "Matte" },
];
const SIDE_LR = [
  { value: 1, label: "Left" },
  { value: 2, label: "Right" },
];
const YES_NO = [
  { value: 0, label: "No" },
  { value: 1, label: "Yes" },
];
const COLOR_RAL = [
  { value: 0, label: "No paint" },
  { value: 1, label: "RAL 9005 (black)" },
  { value: 2, label: "RAL 9010 (white)" },
  { value: 3, label: "Custom RAL" },
];
const WATER_CONNECTION = [
  { value: 1, label: "MNPT" },
  { value: 2, label: "FNPT" },
  { value: 3, label: "Flare" },
];
const DAMPER = [
  { value: 0, label: "No damper" },
  { value: 1, label: "Hit-miss damper" },
  { value: 2, label: "Manual damper" },
  { value: 3, label: "VAV damper" },
];
const NOZZLE_TYPE = [
  { value: "A", label: "A — MN" },
  { value: "B", label: "B — SN" },
  { value: "C", label: "C — UN" },
  { value: "D", label: "D — TN" },
];
const LENGTH_2_6 = [2, 3, 4, 5, 6].map((v) => ({ value: v, label: `${v} ft` }));
const LENGTH_C = [
  { value: 2, label: "2 ft" },
  { value: 4, label: "4 ft" },
  { value: 6, label: "6 ft" },
  { value: 8, label: "8 ft" },
  { value: "X", label: "10 ft" }, // 10 ft encoded as "X" so code stays single-char
];
const MERV = [
  { value: "08", label: "MERV 8" },
  { value: "12", label: "MERV 12" },
  { value: "13", label: "MERV 13" },
  { value: "14", label: "MERV 14" },
];

// Helper to build a field
const F = (key, label, options, std, note = "", visibleWhen = null, extra = {}) => ({
  key,
  label,
  type: "select",
  options,
  default: std,
  standard: std,
  note,
  visibleWhen,
  ...extra,
});
const F_RAL = (key, label, std = 0, note = "", visibleWhen = null) => ({
  key, label, type: "ral", options: COLOR_RAL,
  default: std, standard: std, note, visibleWhen,
});
const F_MERV = (key, label, std = "13", note = "", visibleWhen = null) => ({
  key, label, type: "merv", options: MERV,
  default: std, standard: std, note, visibleWhen,
});
// Auto-populated nozzle type (A/B/C/D) — driven by selection engine. Read-only.
const F_NOZTYPE = (key, label, std = "A", note = "Standard") => ({
  key, label, type: "select", options: NOZZLE_TYPE,
  default: std, standard: std, note, visibleWhen: null, auto: true,
});
// Auto-populated nozzle count — driven by selection engine. Read-only.
const F_NOZCOUNT = (key, label, std = 0, note = "Standard") => ({
  key, label, type: "nozzle_count",
  default: std, standard: std, note, visibleWhen: null, auto: true,
});
// Auto-populated coil pipe count — sourced from project.advanced.no_coil_pipes. Read-only.
const F_COILPIPES = () => ({
  key: "no_coil_pipes", label: "No. Coil Pipes",
  type: "select",
  options: [{ value: 2, label: "Two pipe" }, { value: 4, label: "Four pipe" }],
  default: 2, standard: 2, note: "Standard", visibleWhen: null,
  // auto: value injected by orderingCodeBuilder from project.advanced.no_coil_pipes ?? 2
  auto: true,
});
// Auto-populated coil circuit count — sourced from the selection engine's chosen circuit
// count for this (model, length) group. Read-only.
const F_COILCCTS = () => ({
  key: "coil_circuits", label: "Coil Circuits",
  type: "select",
  options: [{ value: 1, label: "Single" }, { value: 2, label: "Dual" }],
  default: 2, standard: 2, note: "Standard", visibleWhen: null,
  // auto: value injected by orderingCodeBuilder from the variant's engine-selected circuits
  auto: true,
});

// Engineer-picked air-inlet (spigot) diameter for ACB-family units (Models
// C, C1, D, H, I-T/I-L/I-F, L-L/L-T/L-F). 4" / 6" / 8" / 10". Encoded as
// parens-wrapped digits — the same nozzle_count format — so two-digit "10"
// stays unambiguous in the printed code.
//
// Behavioral note: NC depends on this value only for the spigot-dependent
// models (C / C1 / D). On flow-only ACB models the field appears in the
// ordering code for completeness but doesn't change NC.
const F_AIRINLET = (std = 6) => ({
  key: "air_inlet_size", label: "Air Inlet (Spigot)",
  type: "nozzle_count",
  options: [
    { value: 4, label: '4"' },
    { value: 6, label: '6"' },
    { value: 8, label: '8"' },
    { value: 10, label: '10"' },
  ],
  default: std, standard: std, note: "Standard", visibleWhen: null,
});

// Visibility predicates — receive the full config { base, accessory }.
const has = (group, key, allowed) => (cfg) => {
  const v = cfg?.[group]?.[key];
  return allowed.includes(v);
};
const hasBase = (key, allowed) => has("base", key, allowed);
const enclosureOn = hasBase("enclosure", [1, 2]);
const enclosureOnYesNo = hasBase("enclosure", [1]);
const grilleOn = hasBase("supply_air_grille", [1]);
// Visibility predicates for fields that live in `accessory` (since valve/actuator
// moved out of base per the new spreadsheet).
const accValveOn = (cfg) => [1, 2, 3].includes(cfg?.accessory?.valve);
const accActuatorOn = (cfg) => [1, 2].includes(cfg?.accessory?.actuator);
const accValveAndActuatorOn = (cfg) => accValveOn(cfg) && accActuatorOn(cfg);
const accControlValveOn = (cfg) => [1, 2, 3].includes(cfg?.accessory?.control_valve);
const accValveActOn = (cfg) => accControlValveOn(cfg) && [1, 2].includes(cfg?.accessory?.actuator);
const floorMounted = hasBase("mounting", [2]);
const damperOn = hasBase("damper", [1, 2, 3]);
const anyGrilleNF = (cfg) =>
  cfg?.accessory?.ra_grille === 1 || cfg?.accessory?.sa_grille === 1;
const accEnclosureOn = (cfg) => cfg?.accessory?.enclosure_color != null
  && [1, 2, 3].includes(cfg?.accessory?.enclosure_color);

// =====================================
// MODEL S / S-HW  (spreadsheet sheet "Model S")
// =====================================
// Base ex.: "B - S-HW4211110"  (Length, Enclosure, Mounting, Water connection,
//                                Water module, Controls, Communication)
// Accessory ex.: "A-111100000100100100011"  (21 digits)
const MODEL_S = {
  id: "S",
  display: "Model S / S-HW",
  unitModels: ["Model S", "Model S-HW"],
  basePrefix: "B-S",
  accessoryPrefix: "A-",
  baseFields: [
    F("length", "Length", LENGTH_2_6, 4, "Standard"),
    F_COILPIPES(),
    F_COILCCTS(),
    F("enclosure", "Enclosure", [
      { value: 0, label: "No enclosure" },
      { value: 1, label: "Perforated front" },
      { value: 2, label: "Solid front" },
    ], 2, "Standard"),
    F("mounting", "Mounting", [
      { value: 1, label: "Wall mounting" },
      { value: 2, label: "Floor mounting" },
    ], 1, "Standard"),
    F("water_connection", "Water connection", WATER_CONNECTION, 1, "Standard"),
    F("water_module", "Water module", YES_NO, 1, "Standard"),
    F("controls", "Controls", YES_NO, 1, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
  ],
  // 21 accessory items — exact order from spreadsheet.
  accessoryFields: [
    F("piping_connection", "Piping connection", SIDE_LR, 1, "Factory Installed"),     //  1
    F("valve", "Valve", VALVE, 1, "Standard"),                                          //  2
    F("actuator", "Actuator", ACTUATOR, 1, "Standard", accValveOn),                     //  3
    F("open_close", "Open / Close", OPEN_CLOSE, 1, "Standard", accValveAndActuatorOn),  //  4
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),             //  5
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                          //  6
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                  //  7
    F("pipe_insulation", "Pipe insulation", YES_NO, 0, "Factory Installed"),            //  8
    F("condensate_pump", "Condensate pump", YES_NO, 0, "Factory Installed"),            //  9
    F("condensate_safety", "Condensate safety level switch", YES_NO, 0, "Factory Installed"), // 10
    F("condensate_detection", "Condensate detection sensor", YES_NO, 0, "Factory Installed"), // 11
    F("supply_water_temp", "Supply water temperature sensor", YES_NO, 0, "Factory Installed"),// 12
    F("lint_filter", "Lint filter", YES_NO, 0, "Standard"),                             // 13
    F("return_air_temp", "Return air temperature sensor", YES_NO, 0, "Factory Installed"),    // 14
    F("power_supply_24v", "24 VDC power supply", YES_NO, 0, "Separate in a box"),       // 15
    F("thermostat", "Thermostat", [
      { value: 0, label: "No thermostat" },
      { value: 1, label: "Integrated in enclosure" },
      { value: 2, label: "Wall mounted" },
    ], 1, "Standard"),                                                                  // 16
    F("coil_air_bleed", "Coil air bleed", YES_NO, 0, "Factory Installed"),              // 17
    F("coil_paint", "Coil paint (black)", YES_NO, 0, "Factory Installed"),              // 18
    F("usb_type_c", "USB-Type C programming connection", YES_NO, 0, "Factory Installed"),//19
    F_RAL("enclosure_color", "Enclosure color", 0, "Standard", enclosureOn),            // 20
    F("enclosure_finish", "Enclosure finish", FINISH, 1, "Standard", enclosureOn),      // 21
  ],
};

// MODEL_S_HW alias retained — S and S-HW share an identical catalog.
const MODEL_S_HW = MODEL_S;

// =====================================
// MODEL O-1 / O-2  (spreadsheet sheet "Model O")
// =====================================
// Base ex.: "B - O-2 301131101" (Length, Mode of op, Ducted, SA grille,
//                                Water connection, Water module, Controls,
//                                Communication, Coil connection)
// Accessory ex.: "A-100000001100011000111" (21 digits)
const MODEL_O1 = {
  id: "O-1",
  display: "Model O-1 / O-2",
  unitModels: ["Model O-1", "Model O-2"],
  basePrefix: "B-O-1",
  accessoryPrefix: "A-",
  baseFields: [
    F("length", "Length", LENGTH_2_6, 3, "Standard"),
    F_COILPIPES(),
    F_COILCCTS(),
    F("mode_op", "Mode of operation", MODE, 0, "Standard"),
    F("ducted", "Ducted connection", YES_NO, 1, "Standard"),
    F("supply_air_grille", "Supply air grille", YES_NO, 1, "Standard"),
    F("water_connection", "Water connection", WATER_CONNECTION, 3, "Standard"),
    F("water_module", "Water module", YES_NO, 1, "Standard"),
    F("controls", "Controls", YES_NO, 1, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    // Coil connection — relevant ONLY for Model O-2 (dual coils).
    F("coil_connection", "Coil connection (O-2 only)", [
      { value: 1, label: "Serial" },
      { value: 2, label: "Parallel" },
    ], 1, "Standard"),
  ],
  // 21 accessory items.
  accessoryFields: [
    F("piping_connection", "Piping connection", SIDE_LR, 1, "Standard"),                //  1
    F("valve", "Valve", VALVE, 0, "Factory Installed"),                                  //  2
    F("actuator", "Actuator", ACTUATOR, 0, "Factory Installed", accValveOn),             //  3
    F("open_close", "Open / Close", OPEN_CLOSE, 0, "Factory Installed", accValveAndActuatorOn), //  4
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),              //  5
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                           //  6
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                   //  7
    F("pipe_insulation", "Pipe insulation", YES_NO, 0, "Factory Installed"),             //  8
    F("condensate_pump", "Condensate pump", YES_NO, 0, "Factory Installed"),             //  9
    F("condensate_safety", "Condensate safety level switch", YES_NO, 0, "Factory Installed"), // 10
    F("condensate_detection", "Condensate detection sensor", YES_NO, 0, "Factory Installed"), // 11
    F("supply_water_temp", "Supply water temperature sensor", YES_NO, 0, "Factory Installed"),// 12
    F("power_supply_24v", "24 VDC power supply", YES_NO, 0, "Separate in a box"),        // 13
    F("thermostat", "Thermostat", [
      { value: 0, label: "No thermostat" },
      { value: 1, label: "Wall mounted" },
    ], 0, "Separate in a box"),                                                          // 14
    F("sliders", "Sliders", YES_NO, 0, "Separate in a box"),                             // 15
    F("coil_air_bleed", "Coil air bleed", YES_NO, 0, "Standard"),                        // 16
    F("coil_paint", "Coil paint (black)", YES_NO, 0, "Standard"),                        // 17
    F("gripple", "Gripple hanging kit", YES_NO, 0, "Separate in a box"),                 // 18
    F("mounting_frame", "Mounting frame", YES_NO, 0, "Separate in a box"),               // 19
    F_RAL("grille_color", "Grille color", 0, "Standard", grilleOn),                      // 20
    F("grille_finish", "Grille finish", FINISH, 1, "Standard", grilleOn),                // 21
  ],
};

// MODEL_O2 alias — O-1 and O-2 share an identical catalog (encoder reads
// the room's unit_model to print the correct B-O-1/B-O-2 prefix).
const MODEL_O2 = MODEL_O1;

// =====================================
// MODEL I (I-t / I-f / I-l)  (spreadsheet sheet "Model I")
// =====================================
// Base ex.: "B - I-t 3A(34)131111"  (Length, NozType, NumNozzles, Mounting,
//                                    Damper, Water connection, Water module,
//                                    Controls, Communication)
// Accessory ex.: "A-00111121100001100001000" (23 digits)
const MODEL_I = {
  id: "I",
  display: "Model I (T / F / L)",
  unitModels: ["Model I-L", "Model I-T", "Model I-F"],
  basePrefix: "B-I",
  accessoryPrefix: "A-",
  baseFields: [
    F("length", "Length", LENGTH_2_6, 3, "Standard"),
    F_COILPIPES(),
    F_COILCCTS(),
    F_NOZTYPE("nozzle_type", "Nozzle type"),                       // auto from engine
    F_NOZCOUNT("num_nozzles", "Number of nozzles"),                // auto from engine
    F("mounting", "Mounting", [
      { value: 1, label: "Wall mounting" },
      { value: 2, label: "Floor mounting" },
    ], 1, "Standard"),
    F("damper", "Damper", DAMPER, 0, "Standard"),
    F("water_connection", "Water connection", WATER_CONNECTION, 1, "Standard"),
    F("water_module", "Water module", YES_NO, 0, "Standard"),
    F("controls", "Controls", YES_NO, 0, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    F_AIRINLET(),                                                  // spigot bore (NC for I-* is flow-only)
  ],
  // 23 accessory items — exact order from spreadsheet.
  accessoryFields: [
    F("feet", "Feet", [
      { value: 0, label: "No feet" },
      { value: 1, label: "Levelling feet" },
      { value: 2, label: "Bolt-down levelling feet" },
    ], 0, "Separate in a box", floorMounted),                                            //  1
    F("duct_connection", "Duct connection", [
      { value: 0, label: "Both" },
      { value: 1, label: "Left" },
      { value: 2, label: "Right" },
    ], 0, "Standard"),                                                                   //  2
    F("damper_location", "Damper location", SIDE_LR, 1, "Standard", damperOn),           //  3
    F("air_plug", "Air plug", YES_NO, 0, "Separate in a box"),                           //  4
    F("lint_filter", "Lint filter", YES_NO, 0, "Standard"),                              //  5
    F("control_valve", "Control valve", VALVE, 0, "Separate in a box"),                  //  6
    F("actuator", "Actuator", ACTUATOR, 0, "Separate in a box", accControlValveOn),      //  7
    F("valve_open_close", "Valve Open / Close", OPEN_CLOSE, 0, "Separate in a box", accValveActOn), //  8
    F("piping_connection", "Piping connection", SIDE_LR, 1, "Factory Installed"),        //  9
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),              // 10
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                           // 11
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                   // 12
    F("pipe_insulation", "Pipe insulation", YES_NO, 0, "Factory Installed"),             // 13
    F("wall_thermostat", "Wall thermostat", [
      { value: 0, label: "No thermostat" },
      { value: 1, label: "Wall mounted" },
    ], 0, "Separate in a box"),                                                          // 14
    F("condensate_safety", "Condensate safety level switch", YES_NO, 0, "Factory Installed"), // 15
    F("condensate_detection", "Condensate detection sensor", YES_NO, 0, "Factory Installed"), // 16
    F("supply_water_temp", "Supply water temperature sensor", YES_NO, 0, "Factory Installed"),// 17
    F("return_air_temp", "Return air temperature sensor", YES_NO, 0, "Factory Installed"),    // 18
    F("power_supply_24v", "24 VDC power supply", YES_NO, 0, "Separate in a box"),        // 19
    F("coil_air_bleed", "Coil air bleed", YES_NO, 0, "Factory Installed"),               // 20
    F("coil_paint", "Coil paint (black)", YES_NO, 0, "Factory Installed"),               // 21
    F("pneumatic_dp", "Pneumatic DP sensor", YES_NO, 0, "Factory Installed"),            // 22
    F("usb_type_c", "USB-Type C programming connection", YES_NO, 0, "Factory Installed"),// 23
  ],
};

// =====================================
// MODEL L (L-l / L-f / L-t)  (spreadsheet sheet "Model L")
// =====================================
// Base ex.: "B - L-t 3A(34)31111"  (Length, NozType, NumNozzles, Damper,
//                                   Water connection, Water module, Controls,
//                                   Communication)
// Accessory ex.: "A-1111112110000110001" (19 digits)
const MODEL_L = {
  id: "L",
  display: "Model L (L / F / T)",
  unitModels: ["Model L-L", "Model L-T", "Model L-F"],
  basePrefix: "B-L",
  accessoryPrefix: "A-",
  baseFields: [
    F("length", "Length", LENGTH_2_6, 3, "Standard"),
    F_COILPIPES(),
    F_COILCCTS(),
    F_NOZTYPE("nozzle_type", "Nozzle type"),                       // auto from engine
    F_NOZCOUNT("num_nozzles", "Number of nozzles"),                // auto from engine
    F("damper", "Damper", DAMPER, 0, "Standard"),
    F("water_connection", "Water connection", WATER_CONNECTION, 1, "Standard"),
    F("water_module", "Water module", YES_NO, 0, "Standard"),
    F("controls", "Controls", YES_NO, 0, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    F_AIRINLET(),                                                  // spigot bore (NC for L-* is flow-only)
  ],
  // 19 accessory items — exact order from spreadsheet.
  accessoryFields: [
    F("duct_connection", "Duct connection", [
      { value: 0, label: "Both" },
      { value: 1, label: "Left" },
      { value: 2, label: "Right" },
    ], 1, "Factory Installed"),                                                          //  1
    F("damper_location", "Damper location", SIDE_LR, 1, "Factory Installed", damperOn),  //  2
    F("air_plug", "Air plug", YES_NO, 0, "Separate in a box"),                           //  3
    F("lint_filter", "Lint filter", YES_NO, 0, "Standard"),                              //  4
    F("control_valve", "Control valve", VALVE, 0, "Separate in a box"),                  //  5
    F("actuator", "Actuator", ACTUATOR, 0, "Separate in a box", accControlValveOn),      //  6
    F("valve_open_close", "Valve Open / Close", OPEN_CLOSE, 0, "Separate in a box", accValveActOn), //  7
    F("piping_connection", "Piping connection", SIDE_LR, 1, "Factory Installed"),        //  8
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),              //  9
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                           // 10
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                   // 11
    F("pipe_insulation", "Pipe insulation", YES_NO, 0, "Factory Installed"),             // 12
    F("wall_thermostat", "Wall thermostat", [
      { value: 0, label: "No thermostat" },
      { value: 1, label: "Wall mounted" },
    ], 0, "Separate in a box"),                                                          // 13
    F("condensate_safety", "Condensate safety level switch", YES_NO, 0, "Factory Installed"), // 14
    F("condensate_detection", "Condensate detection sensor", YES_NO, 0, "Factory Installed"), // 15
    F("power_supply_24v", "24 VDC power supply", YES_NO, 0, "Separate in a box"),        // 16
    F("coil_air_bleed", "Coil air bleed", YES_NO, 0, "Factory Installed"),               // 17
    F("usb_type_c", "USB-Type C programming connection", YES_NO, 0, "Factory Installed"),// 18
    F("gripple", "Gripple hanging kit", YES_NO, 0, "Separate in a box"),                 // 19
  ],
};

// =====================================
// MODEL H  (spreadsheet sheet "Model H")
// =====================================
// Base ex.: "B - H3A(34)31111"  (Length, NozType, NumNozzles, Damper,
//                                Water connection, Water module, Controls,
//                                Communication)
// Accessory ex.: "A-11111121100001100012" (20 digits — Model L 19 items
// + Condensate Pan as item 20)
const MODEL_H = {
  id: "H",
  display: "Model H",
  unitModels: ["Model H"],
  basePrefix: "B-H",
  accessoryPrefix: "A-",
  baseFields: [
    F("length", "Length", LENGTH_2_6, 3, "Standard"),
    F_COILPIPES(),
    F_COILCCTS(),
    F_NOZTYPE("nozzle_type", "Nozzle type"),
    F_NOZCOUNT("num_nozzles", "Number of nozzles"),
    F("damper", "Damper", DAMPER, 0, "Standard"),
    F("water_connection", "Water connection", WATER_CONNECTION, 1, "Standard"),
    F("water_module", "Water module", YES_NO, 0, "Standard"),
    F("controls", "Controls", YES_NO, 0, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    F_AIRINLET(),                                                  // spigot bore (NC for H is flow-only)
  ],
  // 20 accessory items — Items 1..19 mirror Model L; item 20 = Condensate Pan.
  accessoryFields: [
    F("duct_connection", "Duct connection", [
      { value: 0, label: "Both" },
      { value: 1, label: "Left" },
      { value: 2, label: "Right" },
    ], 1, "Factory Installed"),                                                          //  1
    F("damper_location", "Damper location", SIDE_LR, 1, "Factory Installed", damperOn),  //  2
    F("air_plug", "Air plug", YES_NO, 0, "Separate in a box"),                           //  3
    F("lint_filter", "Lint filter", YES_NO, 0, "Standard"),                              //  4
    F("control_valve", "Control valve", VALVE, 0, "Separate in a box"),                  //  5
    F("actuator", "Actuator", ACTUATOR, 0, "Separate in a box", accControlValveOn),      //  6
    F("valve_open_close", "Valve Open / Close", OPEN_CLOSE, 0, "Separate in a box", accValveActOn), //  7
    F("piping_connection", "Piping connection", SIDE_LR, 1, "Factory Installed"),        //  8
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),              //  9
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                           // 10
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                   // 11
    F("pipe_insulation", "Pipe insulation", YES_NO, 0, "Factory Installed"),             // 12
    F("wall_thermostat", "Wall thermostat", [
      { value: 0, label: "No thermostat" },
      { value: 1, label: "Wall mounted" },
    ], 0, "Separate in a box"),                                                          // 13
    F("condensate_safety", "Condensate safety level switch", YES_NO, 0, "Factory Installed"), // 14
    F("condensate_detection", "Condensate detection sensor", YES_NO, 0, "Factory Installed"), // 15
    F("power_supply_24v", "24 VDC power supply", YES_NO, 0, "Separate in a box"),        // 16
    F("coil_air_bleed", "Coil air bleed", YES_NO, 0, "Factory Installed"),               // 17
    F("usb_type_c", "USB-Type C programming connection", YES_NO, 0, "Factory Installed"),// 18
    F("gripple", "Gripple hanging kit", YES_NO, 0, "Separate in a box"),                 // 19
    F("condensate_pan", "Condensate pan", [
      { value: 0, label: "No" },
      { value: 1, label: "Plain" },
      { value: 2, label: "Insulated" },
    ], 0, "Separate in a box"),                                                          // 20
  ],
};

// =====================================
// MODEL C / C-1  (spreadsheet sheet "Model C")
// =====================================
// Base ex.: "B - C 4A(134)31111"  (Length 2/4/6/8/10, NozType, NumNozzles
//                                  (3-digit), Damper, Water connection,
//                                  Water module, Controls, Communication)
// Accessory ex.: "A-0000001000000001" (16 digits)
const MODEL_C = {
  id: "C",
  display: "Model C / C-1",
  unitModels: ["Model C", "Model C1"],
  basePrefix: "B-C",
  accessoryPrefix: "A-",
  baseFields: [
    F("length", "Length", LENGTH_C, 4, "Standard"),
    F_COILPIPES(),
    F_COILCCTS(),
    F_NOZTYPE("nozzle_type", "Nozzle type"),
    F_NOZCOUNT("num_nozzles", "Number of nozzles"),
    F("damper", "Damper", DAMPER, 0, "Standard"),
    F("water_connection", "Water connection", WATER_CONNECTION, 1, "Standard"),
    F("water_module", "Water module", YES_NO, 0, "Standard"),
    F("controls", "Controls", YES_NO, 0, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    F_AIRINLET(),                                                  // spigot bore — drives NC for C/C1
  ],
  // 16 accessory items — exact order from spreadsheet.
  accessoryFields: [
    F("duct_connection", "Duct connection", [
      { value: 0, label: "Top" },
      { value: 1, label: "Left" },
      { value: 2, label: "Right" },
    ], 0, "Factory Installed"),                                                          //  1
    F("damper_location", "Damper location", [
      { value: 0, label: "Top" },
      { value: 1, label: "Left" },
      { value: 2, label: "Right" },
    ], 0, "Factory Installed", damperOn),                                                //  2
    F("lint_filter", "Lint filter", YES_NO, 0, "Standard"),                              //  3
    F("control_valve", "Control valve", VALVE, 0, "Separate in a box"),                  //  4
    F("actuator", "Actuator", ACTUATOR, 0, "Separate in a box", accControlValveOn),      //  5
    F("valve_open_close", "Valve Open / Close", OPEN_CLOSE, 0, "Separate in a box", accValveActOn), //  6
    F("piping_connection", "Piping connection", SIDE_LR, 1, "Factory Installed"),        //  7
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),              //  8
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                           //  9
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                   // 10
    F("pipe_insulation", "Pipe insulation", YES_NO, 0, "Factory Installed"),             // 11
    F("wall_thermostat", "Wall thermostat", [
      { value: 0, label: "No thermostat" },
      { value: 1, label: "Wall mounted" },
    ], 0, "Separate in a box"),                                                          // 12
    F("power_supply_24v", "24 VDC power supply", YES_NO, 0, "Separate in a box"),        // 13
    F("coil_air_bleed", "Coil air bleed", YES_NO, 0, "Factory Installed"),               // 14
    F("usb_type_c", "USB-Type C programming connection", YES_NO, 0, "Factory Installed"),// 15
    F("gripple", "Gripple hanging kit", YES_NO, 0, "Separate in a box"),                 // 16
  ],
};

// =====================================
// MODEL D  (spreadsheet sheet "Model D" — added 2026-06-23)
// =====================================
// Base ex.: "B - D 442A(134)31111"  (Length 2/4/6/8/10, No. Coil Pipes,
//                                    Coil Circuits, NozType, NumNozzles
//                                    (3-digit), Damper, Water connection,
//                                    Water module, Controls, Communication)
// Accessory ex.: "A-00000100000000001" (17 digits — same first 11 as Model C
//                                       plus Condensate sensor + Supply Water
//                                       Temp sensor inserted before 24V power)
//
// Model D shares Model C's coil + airflow performance data (per Vladimir,
// 2026-06-23). The ordering code is distinct because Model D adds two
// new accessory items, but the selection engine routes Model D → Model C
// in backend/server.py:coil_model_name and airflow_data lookup.
const MODEL_D = {
  id: "D",
  display: "Model D",
  unitModels: ["Model D"],
  basePrefix: "B-D",
  accessoryPrefix: "A-",
  baseFields: [
    F("length", "Length", LENGTH_C, 4, "Standard"),
    F_COILPIPES(),
    F_COILCCTS(),
    F_NOZTYPE("nozzle_type", "Nozzle type"),
    F_NOZCOUNT("num_nozzles", "Number of nozzles"),
    F("damper", "Damper", DAMPER, 0, "Standard"),
    F("water_connection", "Water connection", WATER_CONNECTION, 1, "Standard"),
    F("water_module", "Water module", YES_NO, 0, "Standard"),
    F("controls", "Controls", YES_NO, 0, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    F_AIRINLET(),                                                  // spigot bore — drives NC for D
  ],
  // 17 accessory items — exact order from the 2026-06-23 spreadsheet.
  accessoryFields: [
    F("duct_connection", "Duct connection", [
      { value: 0, label: "Top" },
      { value: 1, label: "Left" },
      { value: 2, label: "Right" },
    ], 0, "Factory Installed"),                                                          //  1
    F("damper_location", "Damper location", [
      { value: 0, label: "Top" },
      { value: 1, label: "Left" },
      { value: 2, label: "Right" },
    ], 0, "Factory Installed", damperOn),                                                //  2
    F("control_valve", "Control valve", VALVE, 0, "Separate in a box"),                  //  3
    F("actuator", "Actuator", ACTUATOR, 0, "Separate in a box", accControlValveOn),      //  4
    F("valve_open_close", "Valve Open / Close", OPEN_CLOSE, 0, "Separate in a box", accValveActOn), //  5
    F("piping_connection", "Piping connection", SIDE_LR, 1, "Factory Installed"),        //  6
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),              //  7
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                           //  8
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                   //  9
    F("pipe_insulation", "Pipe insulation", YES_NO, 0, "Factory Installed"),             // 10
    F("wall_thermostat", "Wall thermostat", [
      { value: 0, label: "No thermostat" },
      { value: 1, label: "Wall mounted (Without CO2)" },
      { value: 2, label: "Wall mounted (With CO2)" },
    ], 0, "Separate in a box"),                                                          // 11
    F("condensate_sensor", "Condensate detection sensor", YES_NO, 0, "Factory Installed"), // 12 — NEW vs Model C
    F("supply_water_temp_sensor", "Supply Water temperature sensor", YES_NO, 0, "Factory Installed"), // 13 — NEW vs Model C
    F("power_supply_24v", "24 VDC power supply", YES_NO, 0, "Separate in a box"),        // 14
    F("coil_air_bleed", "Coil air bleed", YES_NO, 0, "Factory Installed"),               // 15
    F("usb_type_c", "USB-Type C programming connection", YES_NO, 0, "Factory Installed"),// 16
    F("gripple", "Gripple hanging kit", YES_NO, 0, "Separate in a box"),                 // 17
  ],
};

// =====================================
// MODEL N-f  (spreadsheet sheet "Model N-f")
// =====================================
// Base ex.: "B - N-f10111110211"  (Mode, Exhaust air, Secondary coil,
//                                  Mounting, Enclosure, Thermostat, Damper
//                                  control, Airflow meas, UV protection,
//                                  Communication, Acoustic treatment)
// Accessory ex.: "A-11022221(13)10000000210001111" (26 items)
const N_COMMON_BASE = [
  F("mode_op", "Mode of operation", [
    { value: 0, label: "Hydronic cooling and heating" },
    { value: 1, label: "Hydronic cooling with electric heat" },
  ], 0, "Standard"),
];

const MODEL_NF = {
  id: "N-f",
  display: "Model N-f",
  unitModels: ["Model N-f"],
  basePrefix: "B-N-f",
  accessoryPrefix: "A-",
  baseFields: [
    ...N_COMMON_BASE,
    F("exhaust_air", "Exhaust air", YES_NO, 0, "Standard"),
    F("secondary_coil", "Secondary coil", [
      { value: 0, label: "Condensing" },
      { value: 1, label: "Non-condensing" },
    ], 1, "Standard"),
    F("mounting", "Mounting", [
      { value: 1, label: "Surface mounting" },
      { value: 2, label: "In-wall mounting" },
    ], 1, "Standard"),
    F("enclosure", "Enclosure", YES_NO, 1, "Standard"),
    F("thermostat", "Thermostat", [
      { value: 0, label: "Our thermostat" },
      { value: 1, label: "Third-party thermostat" },
    ], 1, "Standard"),
    F("damper_control", "Damper control", [
      { value: 0, label: "No" },
      { value: 1, label: "OA only" },
      { value: 2, label: "OA + EA" },
    ], 1, "Standard"),
    F("airflow_meas", "Air flow measurement", [
      { value: 0, label: "OA only" },
      { value: 1, label: "OA + EA" },
    ], 0, "Standard"),
    F("uv_protection", "UV coil protection", [
      { value: 0, label: "No" },
      { value: 1, label: "PAC only" },
      { value: 2, label: "Both" },
    ], 2, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    F("acoustic_treatment", "Acoustic treatment", YES_NO, 1, "Standard"),
  ],
  // 26 accessory items.
  accessoryFields: [
    F("ra_grille", "RA grille", YES_NO, 1, "Separate in a box"),                          //  1
    F("sa_grille", "SA grille", YES_NO, 1, "Separate in a box"),                          //  2
    F("enclosure_material", "Enclosure / door material", [
      { value: 0, label: "Sheet metal" },
      { value: 1, label: "Wood" },
      { value: 2, label: "Stainless steel" },
    ], 0, "Separate in a box", enclosureOnYesNo),                                         //  3
    F_RAL("enclosure_color", "Enclosure color", 2, "Separate in a box", enclosureOnYesNo),//  4
    F("enclosure_finish", "Enclosure finish", FINISH, 2, "Separate in a box", enclosureOnYesNo), //  5
    F_RAL("grille_color", "Grille color", 2, "Separate in a box", anyGrilleNF),           //  6
    F("grille_finish", "Grille finish", FINISH, 2, "Separate in a box", anyGrilleNF),     //  7
    F("sa_discharge", "SA discharge", YES_NO, 1, "Factory Installed"),                    //  8
    F_MERV("merv_filter", "Filter quality (MERV)", "13", "Factory Installed"),            //  9
    F("air_temp_humid", "Air temperature & humidity", [
      { value: 0, label: "No" },
      { value: 1, label: "SA only" },
      { value: 2, label: "SA + EA + RA" },
    ], 0, "Factory Installed"),                                                           // 10
    F("co2_sensors", "CO₂ sensors", [
      { value: 0, label: "No" },
      { value: 1, label: "OA only" },
      { value: 2, label: "RA only" },
      { value: 3, label: "OA + RA" },
    ], 0, "Factory Installed"),                                                           // 11
    F("pm25_sensors", "PM2.5 sensors", [
      { value: 0, label: "No" },
      { value: 1, label: "OA only" },
      { value: 2, label: "RA only" },
      { value: 3, label: "OA + RA" },
    ], 0, "Factory Installed"),                                                           // 12
    F("formaldehyde", "Formaldehyde sensors", YES_NO, 0, "Factory Installed"),            // 13
    F("voc", "VOC sensor", YES_NO, 0, "Factory Installed"),                               // 14
    F("bipolar_ionization", "Bipolar ionization", YES_NO, 0, "Factory Installed"),        // 15
    F("water_temp_sensors", "Water temperature sensors", [
      { value: 0, label: "No" },
      { value: 1, label: "EWT only" },
      { value: 2, label: "EWT and LWT" },
    ], 0, "Factory Installed"),                                                           // 16
    F("six_way_valve", "6-way valve", YES_NO, 0, "Separate in a box"),                    // 17
    F("oa_connection", "OA connection", [
      { value: 1, label: "Left" },
      { value: 2, label: "Bottom" },
    ], 2, "Factory Installed"),                                                           // 18
    F("piping_connection", "Piping connection", [
      { value: 1, label: "Top" },
      { value: 2, label: "Bottom" },
    ], 1, "Factory Installed"),                                                           // 19
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),               // 20
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                            // 21
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                    // 22
    F("condensate_safety", "Condensate safety switch", [
      { value: 0, label: "No" },
      { value: 1, label: "PAC only" },
      { value: 2, label: "Both" },
    ], 0, "Factory Installed"),                                                           // 23
    F("power_supply_24v", "24 VDC power supply", YES_NO, 1, "Separate in a box"),         // 24
    F("pac_paint", "PAC paint (black)", YES_NO, 1, "Factory Installed"),                  // 25
    F("preventative_maintenance", "Preventative maintenance kit", YES_NO, 1, "Factory Installed"), // 26
  ],
};

// =====================================
// MODEL N  (spreadsheet sheet "Model N")
// =====================================
// Base ex.: "B - N101111102110"  (Mode, Secondary coil, Mounting, Enclosure,
//                                 Thermostat, Damper control, Airflow meas,
//                                 UV protection, Communication, Acoustic
//                                 treatment, Condensate removal)
// Accessory ex.: "A-11022221(13)10000000210001111" (29 items)
const MODEL_N = {
  id: "N",
  display: "Model N",
  unitModels: ["Model N"],
  basePrefix: "B-N",
  accessoryPrefix: "A-",
  baseFields: [
    ...N_COMMON_BASE,
    F("secondary_coil", "Secondary coil", [
      { value: 0, label: "Condensing" },
      { value: 1, label: "Non-condensing" },
    ], 0, "Standard"),
    F("mounting", "Mounting", [
      { value: 1, label: "Surface mounting" },
      { value: 2, label: "In-wall mounting" },
    ], 1, "Standard"),
    F("enclosure", "Enclosure", YES_NO, 1, "Standard"),
    F("thermostat", "Thermostat", [
      { value: 0, label: "Our thermostat" },
      { value: 1, label: "Third-party thermostat" },
    ], 1, "Standard"),
    F("damper_control", "Damper control", [
      { value: 0, label: "No" },
      { value: 1, label: "OA only" },
      { value: 2, label: "OA + EA" },
    ], 1, "Standard"),
    F("airflow_meas", "Air flow measurement", [
      { value: 0, label: "OA only" },
      { value: 1, label: "OA + EA" },
    ], 0, "Standard"),
    F("uv_protection", "UV coil protection", [
      { value: 0, label: "No" },
      { value: 1, label: "PAC only" },
      { value: 2, label: "Both" },
    ], 2, "Standard"),
    F("communication", "Communication", COMMUNICATION, 0, "Standard"),
    F("acoustic_treatment", "Acoustic treatment", YES_NO, 1, "Standard"),
    F("condensate_removal", "Condensate removal", YES_NO, 0, "Standard"),
  ],
  // 29 accessory items — exact order from spreadsheet.
  accessoryFields: [
    F("ra_grille", "RA grille", YES_NO, 1, "Separate in a box"),                          //  1
    F("sa_grille", "SA grille", YES_NO, 1, "Separate in a box"),                          //  2
    F("enclosure_material", "Enclosure / door material", [
      { value: 0, label: "Sheet metal" },
      { value: 1, label: "Wood" },
      { value: 2, label: "Stainless steel" },
    ], 0, "Separate in a box", enclosureOnYesNo),                                         //  3
    F_RAL("enclosure_color", "Enclosure color", 2, "Separate in a box", enclosureOnYesNo),//  4
    F("enclosure_finish", "Enclosure finish", FINISH, 2, "Separate in a box", enclosureOnYesNo), //  5
    F_RAL("grille_color", "Grille color", 2, "Separate in a box", anyGrilleNF),           //  6
    F("grille_finish", "Grille finish", FINISH, 2, "Separate in a box", anyGrilleNF),     //  7
    F("heat_recovery", "Heat recovery", [
      { value: 0, label: "ERV" },
      { value: 1, label: "HRV" },
    ], 1, "Factory Installed"),                                                           //  8
    F("frost_protection", "Frost protection", YES_NO, 0, "Factory Installed"),            //  9
    F("sa_discharge", "SA discharge", YES_NO, 1, "Factory Installed"),                    // 10
    F_MERV("merv_filter", "Filter quality (MERV)", "13", "Factory Installed"),            // 11
    F("hr_efficiency", "Heat recovery efficiency monitor", YES_NO, 0, "Factory Installed"),// 12
    F("air_temp_humid", "Air temperature & humidity", [
      { value: 0, label: "No" },
      { value: 1, label: "SA only" },
      { value: 2, label: "SA + EA + RA" },
    ], 0, "Factory Installed"),                                                           // 13
    F("co2_sensors", "CO₂ sensors", [
      { value: 0, label: "No" },
      { value: 1, label: "OA only" },
      { value: 2, label: "RA only" },
      { value: 3, label: "OA + RA" },
    ], 0, "Factory Installed"),                                                           // 14
    F("pm25_sensors", "PM2.5 sensors", [
      { value: 0, label: "No" },
      { value: 1, label: "OA only" },
      { value: 2, label: "RA only" },
      { value: 3, label: "OA + RA" },
    ], 0, "Factory Installed"),                                                           // 15
    F("formaldehyde", "Formaldehyde sensors", YES_NO, 0, "Factory Installed"),            // 16
    F("voc", "VOC sensor", YES_NO, 0, "Factory Installed"),                               // 17
    F("bipolar_ionization", "Bipolar ionization", YES_NO, 0, "Factory Installed"),        // 18
    F("water_temp_sensors", "Water temperature sensors", [
      { value: 0, label: "No" },
      { value: 1, label: "EWT only" },
      { value: 2, label: "EWT and LWT" },
    ], 0, "Factory Installed"),                                                           // 19
    F("six_way_valve", "6-way valve", YES_NO, 0, "Separate in a box"),                    // 20
    F("oa_connection", "OA connection", [
      { value: 1, label: "Left" },
      { value: 2, label: "Bottom" },
    ], 2, "Factory Installed"),                                                           // 21
    F("piping_connection", "Piping connection", [
      { value: 1, label: "Top" },
      { value: 2, label: "Bottom" },
    ], 1, "Factory Installed"),                                                           // 22
    F("shutoff_valves", "Shut-off valves", YES_NO, 0, "Separate in a box"),               // 23
    F("strainer", "Strainer", YES_NO, 0, "Separate in a box"),                            // 24
    F("flex_hoses", "Flexible hoses", YES_NO, 0, "Separate in a box"),                    // 25
    F("condensate_safety", "Condensate safety switch", [
      { value: 0, label: "No" },
      { value: 1, label: "PAC only" },
      { value: 2, label: "Both" },
    ], 0, "Factory Installed"),                                                           // 26
    F("power_supply_24v", "24 VDC power supply", YES_NO, 1, "Separate in a box"),         // 27
    F("pac_paint", "PAC paint (black)", YES_NO, 1, "Factory Installed"),                  // 28
    F("preventative_maintenance", "Preventative maintenance kit", YES_NO, 1, "Factory Installed"), // 29
  ],
};

// =====================================
// Catalog & lookup
// =====================================
export const ORDERING_CATALOG = [
  MODEL_S,    // S and S-HW share this spec
  MODEL_O1,   // O-1 and O-2 share this spec
  MODEL_I,
  MODEL_L,
  MODEL_H,
  MODEL_C,
  MODEL_D,
  MODEL_N,
  MODEL_NF,
];

// Map a room.unit_model string to the ordering-code spec.
export function specForUnitModel(unitModel) {
  if (!unitModel) return null;
  return ORDERING_CATALOG.find((m) => m.unitModels.includes(unitModel)) || null;
}

// Default option selections for a spec (initial form state).
// Returns { base, accessory, accessoryInstall } where `accessoryInstall` maps
// each accessory field key → "factory" | "box", seeded from the field's
// `note` ("Factory Installed" → factory, "Separate in a box" → box). The
// engineer can override this per accessory in the Configurator UI.
export function defaultsFor(spec) {
  if (!spec) return { base: {}, accessory: {}, accessoryInstall: {} };
  const base = {};
  spec.baseFields.forEach((f) => { base[f.key] = f.default; });
  const accessory = {};
  const accessoryInstall = {};
  spec.accessoryFields.forEach((f) => {
    accessory[f.key] = f.default;
    const note = (f.note || "").toLowerCase();
    if (note.includes("factory")) accessoryInstall[f.key] = "factory";
    else if (note.includes("separate") || note.includes("box")) accessoryInstall[f.key] = "box";
  });
  return { base, accessory, accessoryInstall };
}

// Public helper to decide whether an accessory field exposes the FI/Box
// override. True iff the spec's `note` mentions either of the two phrases.
export function installModeForField(field) {
  const n = (field?.note || "").toLowerCase();
  if (n.includes("factory")) return "factory";
  if (n.includes("separate") || n.includes("box")) return "box";
  return null;
}

// Parse a backend nozzle_label like "A-22" or "B-134" into { type, count }.
export function parseNozzleLabel(label) {
  if (!label || typeof label !== "string") return null;
  const m = label.match(/^([A-D])\s*-\s*(\d+)$/i);
  if (!m) return null;
  return { type: m[1].toUpperCase(), count: parseInt(m[2], 10) };
}

// Eslint hint — these aliases are kept for any historic imports that still
// resolve them. They intentionally point to the merged catalogs above.
export const _legacy_MODEL_S_HW = MODEL_S_HW;
export const _legacy_MODEL_O2 = MODEL_O2;
